/**
 * Mock reviews — at least 3 per popular provider plus a comprehensive batch
 * for the dev-session provider so its review page is well-populated.
 */

import type { Tables } from '@/lib/db.types'
import {
  reviewEntityId, bookingEntityId, providerId, customerUserId, DEV_USER_IDS,
} from './ids'
import { DEV_PROVIDER } from './providers'

type Review = Tables<'reviews'>

const NOW = Date.parse('2026-05-01T08:00:00.000Z')
const T = (days: number) => new Date(NOW + days * 86400_000).toISOString()

interface ReviewSeed {
  reviewerId: string
  providerEntityId: string
  bookingN: number
  rating: number
  comment: string
  response?: string | null
  daysAgo: number
}

const SEEDS: ReviewSeed[] = [
  // Reviews for general providers
  { reviewerId: DEV_USER_IDS.customer, providerEntityId: providerId(15), bookingN: 2,
    rating: 5, comment: 'Snabbt och proffsigt — färdiga på under 3 timmar. Rekommenderas!',
    response: 'Tack så mycket! Hör av dig om du behöver hjälp igen.', daysAgo: 13 },
  { reviewerId: DEV_USER_IDS.customer, providerEntityId: providerId(6), bookingN: 3,
    rating: 5, comment: 'Helt fantastiskt resultat på målningen, även den extra hallen ingick i priset.',
    response: 'Tack Sigge, väldigt roligt att höra! Lycka till med det nya hemmet.', daysAgo: 49 },
  { reviewerId: customerUserId(6), providerEntityId: providerId(9), bookingN: 5,
    rating: 5, comment: 'Fixade min dator på distans inom 45 minuter. Mycket pedagogisk genom hela processen.',
    response: null, daysAgo: 11 },
  { reviewerId: customerUserId(1), providerEntityId: providerId(10), bookingN: 6,
    rating: 5, comment: 'Emma räddade min sons matteresultat. Tydlig pedagogik och tålamod.',
    response: 'Tack! Stort lycka till framöver med matten.', daysAgo: 33 },
  { reviewerId: customerUserId(2), providerEntityId: providerId(2), bookingN: 1,
    rating: 5, comment: 'Bästa städfirman jag använt. Punktliga, noggranna och vänliga.',
    response: 'Tusen tack! Vi ser fram emot nästa städning.', daysAgo: 22 },
  { reviewerId: customerUserId(3), providerEntityId: providerId(1), bookingN: 1,
    rating: 4, comment: 'Snabba och effektiva. En liten skråma på dörrkarmen, men de tog ansvar direkt.',
    response: 'Tack för feedback — vi är glada att vi kunde ordna det med försäkringen.', daysAgo: 18 },
  { reviewerId: customerUserId(4), providerEntityId: providerId(5), bookingN: 1,
    rating: 5, comment: 'Helt nytt kök, allt enligt skiss. ROT-avdrag fungerade smidigt.',
    response: null, daysAgo: 30 },
  { reviewerId: customerUserId(5), providerEntityId: providerId(3), bookingN: 1,
    rating: 5, comment: 'Akut elhjälp på söndagskvällen — kom inom en timme.',
    response: 'Glad att vi kunde hjälpa till!', daysAgo: 9 },
  { reviewerId: customerUserId(7), providerEntityId: providerId(7), bookingN: 1,
    rating: 5, comment: 'Snabb och prisvärd gräsklippning. Vi har nu säsongsavtal.',
    response: null, daysAgo: 17 },
  { reviewerId: customerUserId(8), providerEntityId: providerId(8), bookingN: 1,
    rating: 5, comment: 'Vår labrador är som hemma hos dem. Otroligt trygga och kärleksfulla.',
    response: 'Tack Albin! Hälsa Bosse från oss.', daysAgo: 25 },

  // ── Dev provider reviews (10+ varied) ─────────────────────────────
  { reviewerId: customerUserId(2), providerEntityId: DEV_PROVIDER.id, bookingN: 7,
    rating: 5, comment: 'Sigge gjorde ett otroligt jobb. Snabbt, rent och proffsigt utfört.',
    response: 'Tack Magnus! Hör av dig om något mer behöver fixas.', daysAgo: 35 },
  { reviewerId: customerUserId(3), providerEntityId: DEV_PROVIDER.id, bookingN: 8,
    rating: 5, comment: 'Mycket noggrann. Lyssnade på alla våra önskemål och kom med bra förslag.',
    response: null, daysAgo: 20 },
  { reviewerId: customerUserId(4), providerEntityId: DEV_PROVIDER.id, bookingN: 9,
    rating: 5, comment: 'TV-bänken blev exakt som jag drömt om. Toppenkvalitet på finishen.',
    response: 'Tack Filip — den blev verkligen snygg!', daysAgo: 9 },
  { reviewerId: customerUserId(5), providerEntityId: DEV_PROVIDER.id, bookingN: 7,
    rating: 4, comment: 'Bra jobb och bra kommunikation. Lite försenat med en timme men hör hör.',
    response: 'Ledsen för förseningen Hanna — buss-strul. Glad att slutresultatet blev bra!', daysAgo: 46 },
  { reviewerId: customerUserId(6), providerEntityId: DEV_PROVIDER.id, bookingN: 8,
    rating: 5, comment: 'Snabb, vänlig och kunnig. Klart över förväntan.',
    response: null, daysAgo: 53 },
  { reviewerId: customerUserId(7), providerEntityId: DEV_PROVIDER.id, bookingN: 7,
    rating: 5, comment: 'Renoverade hela badrummet — prick på tiden och inom budget.',
    response: 'Tack Tilde, det var ett kul projekt!', daysAgo: 66 },
  { reviewerId: customerUserId(8), providerEntityId: DEV_PROVIDER.id, bookingN: 9,
    rating: 5, comment: 'Otroligt nöjda. Sigge är så lätt att jobba med.',
    response: null, daysAgo: 78 },
  { reviewerId: customerUserId(1), providerEntityId: DEV_PROVIDER.id, bookingN: 8,
    rating: 5, comment: 'IKEA-PAX monterad på en eftermiddag. Helt utan stress.',
    response: null, daysAgo: 89 },
  { reviewerId: customerUserId(2), providerEntityId: DEV_PROVIDER.id, bookingN: 9,
    rating: 4, comment: 'Bra jobbat. Lite damm efter sig men det är ju snickerijobb.',
    response: 'Tack! Vi förbättrar dammsugningen kontinuerligt.', daysAgo: 102 },
  { reviewerId: customerUserId(3), providerEntityId: DEV_PROVIDER.id, bookingN: 7,
    rating: 5, comment: 'Andra gången vi anlitar Sigge — alltid samma höga kvalitet.',
    response: 'Stort tack Astrid!', daysAgo: 118 },
]

export const MOCK_REVIEWS: Review[] = SEEDS.map((s, i) => {
  const n = i + 1
  const createdAt = T(-s.daysAgo)
  return {
    id: reviewEntityId(n),
    booking_id: bookingEntityId(s.bookingN),
    reviewer_id: s.reviewerId,
    provider_id: s.providerEntityId,
    rating: s.rating,
    comment: s.comment,
    provider_response: s.response ?? null,
    provider_responded_at: s.response ? T(-s.daysAgo + 1) : null,
    created_at: createdAt,
  } satisfies Review
})

export function findReviewsByProviderId(providerEntityId: string): Review[] {
  return MOCK_REVIEWS
    .filter((r) => r.provider_id === providerEntityId)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
}
